import { useCallback, useEffect, useRef, useState } from 'react';
import { ArtEngine } from './engine';
import { ArtworkService, LocalWriteError, type SaveState } from '@/services/artwork';
import { Platform } from '@/platform';
import type { ColoringTemplate } from '@/contracts/content';
import type { Artwork } from '@/contracts/family';

/** Idle debounce before an autosave (spec §9.3 suggests under one second). */
const IDLE_MS = 700;
/** Extra checkpoint during a long continuous drawing session. */
const CHECKPOINT_OPS = 12;

/**
 * Owns the engine, the autosave loop, and the honest save state.
 *
 * Save order (spec §9.3): local write first, and the UI only claims a save
 * once that write has actually resolved. A failure keeps the in-memory canvas
 * intact and surfaces a recovery action.
 */
export function useStudio(account: string, artwork: Artwork | null, template: ColoringTemplate | null) {
  const [engine, setEngine] = useState<ArtEngine | null>(null);
  const [saveState, setSaveState] = useState<SaveState>({ kind: 'idle' });
  const [restoreProblem, setRestoreProblem] = useState<string | null>(null);
  const timer = useRef<number>(0);
  const opsSinceSave = useRef(0);
  const engineRef = useRef<ArtEngine | null>(null);
  const artworkRef = useRef<Artwork | null>(null);

  artworkRef.current = artwork;

  // Build the engine and restore any existing draft.
  useEffect(() => {
    let cancelled = false;
    if (!template || !artwork) {
      setEngine(null);
      return;
    }
    const next = new ArtEngine(template);
    (async () => {
      const draft = await ArtworkService.loadDraft(account, artwork.id);
      if (cancelled) return;
      if (draft) {
        // A draft made against a different template revision is not silently
        // remapped onto new line art (spec §9.4).
        if (draft.templateId === template.id && draft.templateRevision === template.revision) {
          const ok = await next.restore(draft);
          if (!ok) setRestoreProblem('Some of your brush marks could not be loaded, but your colours are here.');
          else setSaveState({ kind: 'saved-local', at: Date.now() });
        } else {
          setRestoreProblem(
            'This picture was made with an earlier version of the drawing. Your original is kept safely in My Art.',
          );
        }
      }
      if (!cancelled) {
        engineRef.current = next;
        setEngine(next);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [account, artwork, template]);

  const flush = useCallback(async () => {
    const e = engineRef.current;
    const a = artworkRef.current;
    if (!e || !a || !e.hasContent) return;
    setSaveState({ kind: 'saving' });
    try {
      await ArtworkService.saveDraft(account, a, e.snapshot());
      opsSinceSave.current = 0;
      setSaveState({ kind: 'saved-local', at: Date.now() });
    } catch (err) {
      const reason = err instanceof LocalWriteError ? err.reason : 'unknown';
      setSaveState({ kind: 'failed', reason, at: Date.now() });
    }
  }, [account]);

  const requestSave = useCallback(() => {
    opsSinceSave.current += 1;
    if (timer.current) window.clearTimeout(timer.current);
    if (opsSinceSave.current >= CHECKPOINT_OPS) {
      void flush();
      return;
    }
    timer.current = window.setTimeout(() => void flush(), IDLE_MS);
  }, [flush]);

  // Backgrounding and force-quit are the moments work is most often lost, so
  // flush synchronously-ish on pause (spec §20.2 "App background/force-quit").
  useEffect(() => {
    const off = Platform.lifecycle.onPause(() => {
      if (timer.current) window.clearTimeout(timer.current);
      void flush();
    });
    return off;
  }, [flush]);

  useEffect(() => {
    return () => {
      if (timer.current) window.clearTimeout(timer.current);
    };
  }, []);

  return { engine, saveState, requestSave, flush, restoreProblem, setSaveState };
}
